import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, Check, Calendar, MapPin, Clock, Mail } from "lucide-react";
import { bookingInfo } from "../mock/mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import ProductCard from "../components/ProductCard";
import { fetchProduct, fetchProducts } from "../lib/api";

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { addToCart } = useCart();
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    setVariantIdx(0);
    setActiveImg(0);
    setQty(1);
    setSize(null);
    fetchProduct(productId)
      .then((p) => {
        setProduct(p);
        return fetchProducts({ category: p.category });
      })
      .then((list) => setRelated((list || []).filter((x) => x.id !== productId).slice(0, 4)))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <main className="max-w-[1400px] mx-auto px-6 mt-16 min-h-[40vh]" />;

  if (!product) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 mt-16 min-h-[40vh]">
        <h1 className="font-serif-brand text-3xl text-[#0e2a1e]">Product not found</h1>
        <Link to="/shop" className="text-[#a4820e] mt-4 inline-block">Back to shop →</Link>
      </main>
    );
  }

  const isService = product.type === "service";
  const variants = product.variants && product.variants.length ? product.variants : null;
  const listPrice = variants ? variants[variantIdx].price : product.price;
  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const chargeNow = isService ? bookingInfo.deposit : listPrice;
  const sizes = product.sizes && product.sizes.length ? product.sizes : null;
  const outOfStock = !isService && typeof product.stock === "number" && product.stock <= 0;

  const handleAdd = () => {
    if (outOfStock) {
      toast({ title: "Out of stock", description: "This item is currently unavailable.", variant: "destructive" });
      return;
    }
    if (sizes && !size) {
      toast({ title: "Please select a size", variant: "destructive" });
      return;
    }
    const parts = [];
    if (variants) parts.push(variants[variantIdx].name);
    if (size) parts.push(`Size ${size}`);
    const suffix = parts.length ? ` (${parts.join(" · ")})` : "";
    const label = `${product.name}${suffix}`;
    addToCart(
      {
        id: `${product.id}${variants ? `-v${variantIdx}` : ""}${size ? `-${size}` : ""}`,
        product_id: product.id,
        variant_index: variants ? variantIdx : null,
        variant: parts.join(" · "),
        size: size || null,
        name: label,
        price: chargeNow,
        listPrice,
        image: product.image,
        type: product.type,
      },
      qty
    );
    toast({ title: "Added to bag", description: `${qty} × ${label}` });
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 mt-10 fade-up">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl bg-white border border-[#0e2a1e]/8">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="flex gap-3 mt-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[#0e2a1e]" : "border-transparent"}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2">
          {isService && product.tagline && (
            <span className="inline-block bg-[#a4d61e]/25 text-[#2f6b1e] text-xs font-semibold px-3 py-1 rounded-full mb-3">
              {product.tagline}
            </span>
          )}
          <h1 className="font-serif-brand text-3xl md:text-4xl font-semibold text-[#0e2a1e]">{product.name}</h1>
          <p className="text-2xl text-[#0e2a1e] mt-4">${listPrice.toFixed(2)}</p>
          {!isService && (
            outOfStock ? (
              <p className="text-sm text-red-600 mt-2 font-semibold">Out of stock</p>
            ) : typeof product.stock === "number" ? (
              <p className="text-sm mt-2 font-medium">
                <span className="text-[#2f6b3f]">In stock</span>
                <span className={`ml-2 ${product.stock <= 5 ? "text-amber-600" : "text-[#0e2a1e]/60"}`}>
                  {product.stock <= 5 ? `Only ${product.stock} left` : `${product.stock} available`}
                </span>
              </p>
            ) : (
              <p className="text-sm text-[#2f6b3f] mt-2 font-medium">{product.inStock ? "In stock" : "Out of stock"}</p>
            )
          )}
          <p className="text-[#0e2a1e]/70 mt-6 leading-relaxed">{product.description}</p>

          {variants && (
            <div className="mt-6">
              <p className="text-sm font-medium text-[#0e2a1e] mb-2">Choose an option</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {variants.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setVariantIdx(i)}
                    className={`text-left px-4 py-3 rounded-lg border transition-colors ${variantIdx === i ? "border-[#0e2a1e] bg-[#0e2a1e] text-[#f4f2ea]" : "border-[#0e2a1e]/20 text-[#0e2a1e] hover:border-[#0e2a1e]/50"}`}
                  >
                    <span className="block text-sm font-medium">{v.name}</span>
                    <span className={`text-sm ${variantIdx === i ? "text-[#a4d61e]" : "text-[#0e2a1e]/60"}`}>${v.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {sizes && (
            <div className="mt-6">
              <p className="text-sm font-medium text-[#0e2a1e] mb-2">Select size</p>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-[52px] px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${size === s ? "border-[#0e2a1e] bg-[#0e2a1e] text-[#f4f2ea]" : "border-[#0e2a1e]/20 text-[#0e2a1e] hover:border-[#0e2a1e]/50"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isService && product.deliverables && product.deliverables.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-[#0e2a1e] mb-3">What you get</p>
              <ul className="space-y-2">
                {product.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#0e2a1e]/75">
                    <Check size={16} className="text-[#2f6b1e] mt-0.5 shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-[#0e2a1e]/20 rounded-full">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-[#0e2a1e] hover:text-[#a4820e] transition-colors" aria-label="Decrease">
                <Minus size={16} />
              </button>
              <span className="w-10 text-center text-[#0e2a1e] font-medium">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 text-[#0e2a1e] hover:text-[#a4820e] transition-colors" aria-label="Increase">
                <Plus size={16} />
              </button>
            </div>
            <button onClick={handleAdd} disabled={outOfStock} className="flex-1 bg-[#0e2a1e] text-[#f4f2ea] font-semibold py-3.5 rounded-full hover:bg-[#164031] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {isService ? `Book — $${bookingInfo.deposit} deposit` : outOfStock ? "Out of stock" : "Add to bag"}
            </button>
          </div>

          {isService ? (
            <div className="mt-10 border-t border-[#0e2a1e]/10 pt-6 space-y-4">
              <div className="flex items-start gap-3 text-sm text-[#0e2a1e]/80">
                <MapPin size={18} className="text-[#2f6b1e] mt-0.5 shrink-0" />
                <span><strong>Where:</strong> {product.location}</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-[#0e2a1e]/80">
                <Clock size={18} className="text-[#2f6b1e] mt-0.5 shrink-0" />
                <span><strong>Turnaround:</strong> {product.turnaround}</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-[#0e2a1e]/80">
                <Mail size={18} className="text-[#2f6b1e] mt-0.5 shrink-0" />
                <span>{bookingInfo.note}</span>
              </div>
              <div className="bg-[#a4d61e]/15 rounded-xl p-4 flex items-start gap-3">
                <Calendar size={18} className="text-[#2f6b1e] mt-0.5 shrink-0" />
                <p className="text-sm text-[#0e2a1e]/85">
                  You pay a <strong>${bookingInfo.deposit} non-refundable deposit</strong> now to lock in your rate (balance due on the day). {bookingInfo.digitalNote}
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-10 border-t border-[#0e2a1e]/10 pt-6">
              <h3 className="font-serif-brand text-lg font-semibold text-[#0e2a1e] mb-3">Pick-up, returns &amp; care</h3>
              <p className="text-sm text-[#0e2a1e]/70 leading-relaxed">
                Orders are <strong>pick-up only</strong> (no shipping).{" "}
                <Link to="/info/returns" className="text-[#a4820e] underline">All sales final</Link>. See{" "}
                <Link to="/info/local-pickup" className="text-[#a4820e] underline">pick-up policy</Link>,{" "}
                <Link to="/info/care" className="text-[#a4820e] underline">care instructions</Link>, and{" "}
                <Link to="/info/sizing" className="text-[#a4820e] underline">sizing guide</Link>.
              </p>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e] mb-6">{isService ? "More sessions" : "You may also like"}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
